import type { CodeErreur } from './erreurs.js';

/**
 * Saisie différée du panneau de contact (docs/UI.md § Écran 3) : une clôture
 * ou une évaluation faite sans réseau part plus tard, avec l'heure de l'appui.
 * Cette heure vient du téléphone : le serveur la borne, et le téléphone
 * applique les mêmes bornes avant de mettre la saisie en file.
 */

/** Écart toléré entre l'horloge du téléphone (recalée sur l'en-tête `Date`) et celle du serveur. */
export const TOLERANCE_HORLOGE_MS = 2 * 60 * 1000;
/** Au-delà, une saisie gardée sur le téléphone ne s'enregistre plus : elle y est effacée. */
export const DELAI_SAISIE_DIFFEREE_JOURS = 7;
export const DELAI_SAISIE_DIFFEREE_MS = DELAI_SAISIE_DIFFEREE_JOURS * 24 * 60 * 60 * 1000;

export type CodeHorodatage = Extract<
  CodeErreur,
  'HORODATAGE_FUTUR' | 'HORODATAGE_AVANT_OUVERTURE' | 'HORODATAGE_TROP_ANCIEN'
>;

export type IssueHorodatage =
  | { ok: true; valeur: Date; ajuste: boolean }
  | { ok: false; code: CodeHorodatage; message: string };

const FORMAT_TUNIS = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Africa/Tunis',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** « 15/09 à 14:32 », heure de Tunis quel que soit le fuseau de l'appareil. */
export function dateHeureDeTunis(date: Date | string | number): string {
  const parties = Object.fromEntries(
    FORMAT_TUNIS.formatToParts(new Date(date)).map((partie) => [partie.type, partie.value]),
  );
  return `${parties.day}/${parties.month} à ${parties.hour}:${parties.minute}`;
}

/**
 * Borne l'heure fournie par le téléphone. Dans l'ordre : en avance de plus de
 * la tolérance → refus ; plus vieille que 7 jours → refus ; antérieure à
 * l'ouverture du contact de plus de la tolérance → refus. Dans la tolérance,
 * l'heure est ramenée entre l'ouverture et l'heure du serveur.
 */
export function bornerHorodatage(
  fourni: string | Date,
  bornes: { debuteLe: Date; maintenant: Date },
): IssueHorodatage {
  const t = typeof fourni === 'string' ? Date.parse(fourni) : fourni.getTime();
  // Les schémas zod n'admettent que des instants lisibles : ceci est une faute d'appel.
  if (Number.isNaN(t)) throw new RangeError(`Horodatage illisible : ${String(fourni)}`);
  const maintenant = bornes.maintenant.getTime();
  const debut = bornes.debuteLe.getTime();

  if (t > maintenant + TOLERANCE_HORLOGE_MS) {
    return {
      ok: false,
      code: 'HORODATAGE_FUTUR',
      message:
        `Heure de saisie (${dateHeureDeTunis(t)}) en avance sur le serveur ` +
        `(${dateHeureDeTunis(maintenant)}) : l’horloge du téléphone est déréglée.`,
    };
  }
  if (t < maintenant - DELAI_SAISIE_DIFFEREE_MS) {
    return {
      ok: false,
      code: 'HORODATAGE_TROP_ANCIEN',
      message: `Saisie du ${dateHeureDeTunis(t)} : plus de 7 jours, elle ne s’enregistre plus en différé.`,
    };
  }
  if (t < debut - TOLERANCE_HORLOGE_MS) {
    return {
      ok: false,
      code: 'HORODATAGE_AVANT_OUVERTURE',
      message:
        `Heure de saisie (${dateHeureDeTunis(t)}) antérieure à l’ouverture du contact ` +
        `(${dateHeureDeTunis(debut)}) : l’horloge du téléphone est déréglée.`,
    };
  }
  const valeur = Math.min(Math.max(t, debut), maintenant);
  return { ok: true, valeur: new Date(valeur), ajuste: valeur !== t };
}

/** Vrai si la saisie date d'avant l'arrivée de plus que la tolérance : elle a attendu le réseau. */
export function saisieDifferee(valeur: Date, maintenant: Date): boolean {
  return maintenant.getTime() - valeur.getTime() > TOLERANCE_HORLOGE_MS;
}

/** Heure d'appui à envoyer : l'heure du serveur estimée, jamais avant l'ouverture du contact. */
export function heureDeSaisie(debuteLe: string, maintenantMs: number): string {
  return new Date(Math.max(maintenantMs, Date.parse(debuteLe))).toISOString();
}
